<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AppointmentModal from 'dashboard/routes/dashboard/calendar/components/AppointmentModal.vue';

const store = useStore();
const { t } = useI18n();

// State
const showModal = ref(false);
const showAppointmentModal = ref(false);
const selectedCalendarId = ref(null);
const preSelectedContact = ref(null);
const conversationId = ref(null);

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get current time rounded to next 30 minutes
const getDefaultTime = () => {
  const now = new Date();
  const minutes = now.getMinutes();
  const roundedMinutes = minutes < 30 ? 30 : 0;
  const hours = minutes < 30 ? now.getHours() : now.getHours() + 1;
  return `${String(hours).padStart(2, '0')}:${String(roundedMinutes).padStart(2, '0')}`;
};

// Computed
const calendars = computed(() => store.getters['calendar/getCalendars']);
// Use store's currentCalendar which has full details (including available_agents) from fetchCalendar
const storeCurrentCalendar = computed(() => store.getters['calendar/getCurrentCalendar']);
const currentCalendar = computed(() => {
  // Prefer store's currentCalendar if it matches our selection (has full details)
  if (storeCurrentCalendar.value && storeCurrentCalendar.value.id === selectedCalendarId.value) {
    return storeCurrentCalendar.value;
  }
  // Fallback to calendars list
  return calendars.value.find(c => c.id === selectedCalendarId.value);
});
const agents = computed(() => currentCalendar.value?.available_agents || []);
const isLoading = computed(() => store.getters['calendar/getUIFlags']?.isFetchingCalendars);

// Methods
const handleOpenScheduleModal = async (data) => {
  preSelectedContact.value = data.contactId ? {
    id: data.contactId,
    name: data.contactName,
  } : null;
  conversationId.value = data.conversationId;
  
  // Fetch calendars if not loaded
  if (calendars.value.length === 0) {
    await store.dispatch('calendar/fetchCalendars');
  }
  
  // If only one calendar, go directly to appointment modal
  if (calendars.value.length === 1) {
    selectedCalendarId.value = calendars.value[0].id;
    await store.dispatch('calendar/fetchCalendar', selectedCalendarId.value);
    showAppointmentModal.value = true;
  } else if (calendars.value.length > 1) {
    // Show calendar selection modal
    showModal.value = true;
  } else {
    // No calendars available
    useAlert(t('CALENDAR.ERRORS.NO_CALENDARS'));
  }
};

const selectCalendar = async (calendarId) => {
  selectedCalendarId.value = calendarId;
  await store.dispatch('calendar/fetchCalendar', calendarId);
  showModal.value = false;
  showAppointmentModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedCalendarId.value = null;
  preSelectedContact.value = null;
  conversationId.value = null;
};

const closeAppointmentModal = () => {
  showAppointmentModal.value = false;
  selectedCalendarId.value = null;
  preSelectedContact.value = null;
  conversationId.value = null;
};

const handleAppointmentCreated = () => {
  useAlert(t('CALENDAR.MESSAGES.APPOINTMENT_CREATED'));
  closeAppointmentModal();
};

// Lifecycle
onMounted(() => {
  emitter.on(BUS_EVENTS.OPEN_SCHEDULE_MODAL, handleOpenScheduleModal);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.OPEN_SCHEDULE_MODAL, handleOpenScheduleModal);
});
</script>

<template>
  <!-- Calendar Selection Modal (when multiple calendars) -->
  <Modal
    v-if="showModal"
    :show="showModal"
    :on-close="closeModal"
    size="small"
  >
    <div class="flex flex-col p-6">
      <h2 class="mb-4 text-lg font-semibold text-n-slate-12">
        {{ t('CALENDAR.SELECT_CALENDAR') }}
      </h2>
      
      <div v-if="isLoading" class="flex justify-center py-8">
        <div class="i-lucide-loader-2 size-8 animate-spin text-n-slate-11" />
      </div>
      
      <div v-else class="flex flex-col gap-2">
        <button
          v-for="calendar in calendars"
          :key="calendar.id"
          class="flex items-center gap-3 rounded-lg border border-n-weak p-3 text-left transition-colors hover:bg-n-alpha-1"
          @click="selectCalendar(calendar.id)"
        >
          <div class="flex size-10 items-center justify-center rounded-lg bg-n-blue-3">
            <div class="i-lucide-calendar size-5 text-n-blue-11" />
          </div>
          <div>
            <h4 class="font-medium text-n-slate-12">{{ calendar.name }}</h4>
            <p v-if="calendar.description" class="text-sm text-n-slate-11">
              {{ calendar.description }}
            </p>
          </div>
        </button>
      </div>
      
      <div class="mt-4 flex justify-end">
        <Button
          variant="ghost"
          :label="t('CALENDAR.ACTIONS.CANCEL')"
          @click="closeModal"
        />
      </div>
    </div>
  </Modal>
  
  <!-- Appointment Modal -->
  <AppointmentModal
    v-if="showAppointmentModal && currentCalendar"
    :calendar="currentCalendar"
    :calendars="calendars"
    :agents="agents"
    :pre-selected-contact="preSelectedContact"
    :initial-date="getTodayDate()"
    :initial-time="getDefaultTime()"
    @close="closeAppointmentModal"
    @created="handleAppointmentCreated"
  />
</template>
